import { Info } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { MatchLikelihood } from "@api/shared-runtime/enums";

export type JobMatch = {
  likelihood: MatchLikelihood | null;
  summary: string | null;
};

type RatedLikelihood =
  | MatchLikelihood.High
  | MatchLikelihood.Medium
  | MatchLikelihood.Low;

type StrengthStyle = {
  label: string;
  /** How many of the three signal bars are filled; a non-color strength cue. */
  filledBars: number;
  className: string;
};

const TOTAL_BARS = 3;

const FALLBACK_SUMMARY = "Based on your profile and job preferences.";
const ACCESSIBLE_LABEL = "Why this job is recommended";

// Visual weight steps down from a tinted, bold pill to a quiet neutral one.
// All use the brand/neutral palette, never amber or red, so a weaker match
// never reads as a warning.
const STRENGTH_STYLES: Record<RatedLikelihood, StrengthStyle> = {
  [MatchLikelihood.High]: {
    label: "Strong match",
    filledBars: 3,
    className:
      "border-primary/25 bg-primary/10 font-semibold text-primary hover:bg-primary/15",
  },
  [MatchLikelihood.Medium]: {
    label: "Good match",
    filledBars: 2,
    className:
      "border-primary/20 bg-white font-medium text-primary hover:bg-primary/5",
  },
  [MatchLikelihood.Low]: {
    label: "Possible match",
    filledBars: 1,
    className:
      "border-slate-200 bg-slate-50 font-medium text-slate-600 hover:bg-slate-100",
  },
};

function getStrengthStyle(
  likelihood: MatchLikelihood | null,
): StrengthStyle | null {
  if (
    likelihood === MatchLikelihood.High ||
    likelihood === MatchLikelihood.Medium ||
    likelihood === MatchLikelihood.Low
  ) {
    return STRENGTH_STYLES[likelihood];
  }
  // Null (not rated yet) and no-match show nothing.
  return null;
}

function SignalBars({ filled }: { filled: number }) {
  return (
    <span className="flex items-end gap-px" aria-hidden="true">
      {Array.from({ length: TOTAL_BARS }, (_, index) => (
        <span
          key={index}
          className={cn(
            "w-[3px] rounded-[1px] bg-current",
            index < filled ? "opacity-100" : "opacity-25",
            index === 0 && "h-1.5",
            index === 1 && "h-2",
            index === 2 && "h-2.5",
          )}
        />
      ))}
    </span>
  );
}

/**
 * Match-strength pill for a recommended job. The whole pill is a popover
 * trigger (a real button, so it works on tap and with the keyboard) that
 * explains why the job was recommended and links to job preferences.
 */
export function MatchStrengthBadge({ match }: { match: JobMatch }) {
  const strength = getStrengthStyle(match.likelihood);
  if (!strength) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${strength.label}. ${ACCESSIBLE_LABEL}`}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            strength.className,
          )}
        >
          <SignalBars filled={strength.filledBars} />
          {strength.label}
          <Info className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 space-y-2 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-slate-900">
          {ACCESSIBLE_LABEL}
        </h3>
        <p className="text-sm text-slate-700">
          {match.summary ?? FALLBACK_SUMMARY}
        </p>
        <p className="text-xs text-slate-500">
          Updating your job preferences improves your recommendations.
        </p>
        <Link
          to="/provider/jobs/preferences"
          className="inline-block text-sm font-medium text-primary underline-offset-2 hover:underline"
        >
          Update job preferences
        </Link>
      </PopoverContent>
    </Popover>
  );
}
